import React, { useState, useEffect } from "react";
import moment from "moment";
import { Icon, Popover } from "antd";
import { numberformatter } from "$ACTIONS/util";
import { InfoCircleOutlined } from "@ant-design/icons";
import { bonusStatusEnum } from "$ACTIONS/promotionRequest";
import { enumBonusGivenType } from "$ACTIONS/promotionRequest";

// 定義圖標的對應表
const ICONS_ASSETS = {
    Sports: `/th/img/promotions/icon/icon-Sports.png`,
    Esports: `/th/img/promotions/icon/icon-Esports.png`,
    P2P: `/th/img/promotions/icon/icon-P2P.png`,
    Fishing: `/th/img/promotions/icon/icon-Fishing.png`,
    InstantGames: `/th/img/promotions/icon/icon-InstantGames.png`,
    KENO: `/th/img/promotions/icon/icon-Lottery.png`,
    SLOT: `/th/img/promotions/icon/icon-Slot.png`,
    LD: `/th/img/promotions/icon/icon-Casino.png`,
};

// 定義類別與對應圖標的映射
const ICONS_MAPPING = {
    SB: ["Sports", "Esports"], // SB 顯示 Sports 和 Esports
    P2P: ["P2P", "InstantGames", "Fishing"], // P2P 顯示 P2P、Fishing 和 InstantGames
    KENO: ["KENO"],
    SLOT: ["SLOT"],
    LD: ["LD"],
};

const CardIcons = ({ productGroups }) => {
    if (!productGroups) return null;

    // 根據第一個 productGroup 確定對應的圖標類別
    const mainGroup = productGroups?.productGroup;
    const relatedGroups = ICONS_MAPPING[mainGroup] || []; // 獲取相關類別

    // 3 個 icon 時使用較小尺寸，否則使用較大尺寸
    const iconClass = relatedGroups.length === 3 ? "small-icon" : "large-icon";

    return (
        <div className="card-icons">
            {relatedGroups.map((group, index) =>
                ICONS_ASSETS[group] ? (
                    <img
                        key={index}
                        src={ICONS_ASSETS[group]}
                        alt={group}
                        className={`card-icon ${iconClass}`}
                    />
                ) : null
            )}
        </div>
    );
};

// 倒计时组件
const CountdownTimer = ({ deadline }) => {
    const [timeLeft, setTimeLeft] = useState("");

    useEffect(() => {
        const interval = setInterval(() => {
            const now = moment().utcOffset(8); // 当前时间，GMT+8
            const end = moment(deadline || undefined).utcOffset(8); // Deadline，GMT+8
            const duration = moment.duration(end.diff(now));

            if (duration.asSeconds() <= 0) {
                setTimeLeft("00 วัน 00:00"); // 倒计时结束，固定格式
                clearInterval(interval); // 倒计时结束时清除定时器
            } else {
                const days = Math.floor(duration.asDays()).toString().padStart(2, "0");
                const hours = duration.hours().toString().padStart(2, "0");
                const minutes = duration.minutes().toString().padStart(2, "0");

                setTimeLeft(`${days} วัน ${hours}:${minutes}`);
            }
        }, 1000);

        return () => clearInterval(interval); // 清除组件卸载时的定时器
    }, [deadline]);

    return <span>{timeLeft}</span>;
};

// 字段映射表
const fieldMappings = {
    common: {
        title: "bonusName",
        amount: "bonusAmount",
        deadline: "expiredDate",
        productGroups: (data) => {
            if (data?.productGroup) {
                // 如果有 wallet，直接包成 Obj
                return { productGroup: data.productGroup };
            }
            return undefined; // 默认返回 undefined
        },
        progress: "progress",
        percentage: "percentage",
        bonusRuleType: "bonusRuleType",
        bonusStatusId: "bonusStatusId",
        bonusGivenType: "bonusGivenType",
    },
    notStarted: {
        title: "campaignNativeName",
        amount: "givenAmount",
        deadline: "claimEndDateUtc",
        productGroups: (data) => {
            if (Array.isArray(data?.productGroups)) {
                return data.productGroups.length > 1 ? undefined : { productGroup: data.productGroups?.[0]?.productGroup };
            }
            return undefined;
        },
    },
    completed: {
        statusTipsMessage: "statusTipsMessage",
    },
};

// 根据 type 和字段映射提取数据
const getMappedData = (data, type) => {
    const commonMapping = fieldMappings.common;
    const specificMapping = fieldMappings[type] || {};
    const combinedMapping = { ...commonMapping, ...specificMapping };

    // 提取字段并处理
    return Object.entries(combinedMapping).reduce((result, [key, mapping]) => {
        if (typeof mapping === "function") {
            result[key] = mapping(data);
        } else {
            result[key] = data[mapping];
        }
        return result;
    }, {});
};

// 此 Card 組件在 MyPromotion & PMA 使用
const MyPromotionCard = ({
    data,
    type,
    onActionClick,
    from = "MyPromotion",
    showFooterMessage = false,
    showMoreMenuId,
    onMoreClick = () => {},
}) => {
    const [mappedData, setMappedData] = useState({});

    useEffect(() => {
        // 调用 getMappedData 并存储到 state
        const result = getMappedData(data, type);
        setMappedData(result);
    }, [data, type]);

    // 提取映射数据
    const {
        title,
        amount,
        deadline,
        productGroups,
        progress,
        percentage,
        bonusRuleType,
        bonusStatusId,
        bonusGivenType,
        statusTipsMessage,
    } = mappedData;

    // 分割 progress 数据
    const [currentProgress, totalProgress] = progress?.split("/") || ["0", "0"];

    // 獲取狀態元素的函數
    const getStatusElement = () => {
        // 狀態配置
        const statusConfig = [
            {
                statuses: [
                    bonusStatusEnum.ForcedToServed,
                    bonusStatusEnum.Complete,
                ],
                text: "สำเร็จ", // 完成
                className: "status-complete",
            },
            {
                statuses: [bonusStatusEnum.Cancelled],
                text: "ยกเลิก", // 取消
                className: "status-cancelled",
            },
            {
                statuses: [bonusStatusEnum.Expired],
                text: "หมดอายุ", // 已過期
                className: "status-expired",
            },
        ];

        const config = statusConfig.find((item) =>
            item.statuses.includes(bonusStatusId)
        );

        if (!config) return null;

        const statusContent = (
            <div className={`status-container ${config.className}`}>
                {bonusRuleType === "PRE" && (
                    <InfoCircleOutlined className="info-icon" />
                )}
                <span className="status-text">{config.text}</span>
            </div>
        );

        // 只有 `bonusRuleType === "PRE"` 才包裹 Popover，否則直接返回 `statusContent`
        return bonusRuleType === "PRE" ? (
            <Popover
                placement="topRight"
                content={statusTipsMessage}
                overlayStyle={{ maxWidth: "220px" }}
            >
                {statusContent}
            </Popover>
        ) : (
            statusContent
        );
    };

    const getInfoLabel = (type, bonusGivenType, bonusRuleType) => {
        switch (type) {
            case "notStarted":
                return "โบนัสรับทันที";
    
            case "inProgress":
            case "completed":
                if (enumBonusGivenType.isFreeSpin(bonusGivenType)) return "ฟรีสปิน";
                if (enumBonusGivenType.isRewardsPoint(bonusGivenType)) return "แต้มรางวัล";
                if (enumBonusGivenType.isMoney(bonusGivenType)) {
                    return bonusRuleType === "PRE" ? "โบนัสรับทันที" : "โบนัสที่มีอยู่";
                }
                return "";

            case "claimable":
                return "โบนัสที่มีอยู่";
    
            default:
                return "";
        }
    };

    const getFormattedAmount = (bonusGivenType, amount) => {
        const prefix = enumBonusGivenType.isMoney(bonusGivenType) ? "฿" : "";
        const formattedAmount = numberformatter(amount, 2, "removeWhenDecimalAllZero");
        return `${prefix} ${formattedAmount}`;
    };
  
    // 渲染卡片 Body
    const renderBody = () => {
        if (type === "notStarted") {
            return (
                <div className="promotion-not-started-section">
                    <div className="card-info">
                        <span className="info-label">
                            {getInfoLabel(type, bonusGivenType, bonusRuleType)}
                        </span>
                        <span className="info-value">{getFormattedAmount(bonusGivenType, amount)}</span>
                    </div>
                    <div className="card-info card-info-time">
                        <span className="info-label">เวลาสิ้นสุดโปรโมชั่น</span>
                        <span className="info-value">
                            {deadline &&
                                moment(deadline)
                                    .utcOffset(8)
                                    .format("DD/MM/YYYY HH:mm [GMT+8]")}
                        </span>
                    </div>
                </div>
            );
        } else if (type === "inProgress") {
            return (
                <div className="promotion-inProgress-section">
                    <div className="card-info">
                        <span className="info-label">
                            {getInfoLabel(type, bonusGivenType, bonusRuleType)}
                        </span>
                        <span className="info-value">{getFormattedAmount(bonusGivenType, amount)}</span>
                    </div>
                    <div className="card-info card-info-time">
                        {deadline && (
                            <>
                                <span className="info-label">
                                    นับถอยหลังก่อน "หมดเวลา"
                                </span>
                                <span className="info-value">
                                    <CountdownTimer deadline={deadline} />
                                </span>
                            </>
                        )}
                    </div>
                </div>
            );
        } else if (type === "claimable") {
            return (
                <div className="promotion-claimable-section">
                    <div className="card-info">
                        <span className="info-label">
                            {getInfoLabel(type, bonusGivenType, bonusRuleType)}
                        </span>
                        <span className="info-value">{getFormattedAmount(bonusGivenType, amount)}</span>
                    </div>
                    <div className="card-info card-info-time">
                        <span className="info-label">
                            นับถอยหลังก่อน "หมดเวลา"
                        </span>
                        <span className="info-value">
                            <CountdownTimer deadline={deadline} />
                        </span>
                    </div>
                </div>
            );
        } else if (type === "completed") {
            const formattedDeadline = deadline
                ? moment(deadline).utcOffset(8).format("DD/MM/YYYY HH:mm [GMT+8]")
                : (enumBonusGivenType.isFreeSpin(bonusGivenType) || enumBonusGivenType.isRewardsPoint(bonusGivenType))
                    ? "ไม่มีกำหนด"
                    : "";

            return (
                <div className="promotion-completed-section">
                    <div className="card-info">
                        <span className="info-label">
                            {getInfoLabel(type, bonusGivenType, bonusRuleType)}
                        </span>
                        <span className="info-value">{getFormattedAmount(bonusGivenType, amount)}</span>
                    </div>
                    <div className="card-info card-info-time">
                        <span className="info-label">
                            เวลาสิ้นสุด: {formattedDeadline}
                        </span>
                        <span className="info-value">
                            {getStatusElement()}
                        </span>
                    </div>
                </div>
            );
        }
    };
    // 定义不同状态的样式
    const renderFooter = () => {
        if (from === "MyPromotion") {
            if (type === "notStarted") {
                return (
                    <div className="promotion-not-started-section">
                        <button
                            className="detail-button"
                            onClick={() => onActionClick(data, "detail")}
                        >
                            {/* 查看详情 */}
                            ดูรายละเอียด
                        </button>
                        <div className="divider" />
                        <button
                            className="start-button"
                            onClick={() => onActionClick(data, "start")}
                        >
                            {/* 开始优惠 */}
                            ขอรับตอนนี้
                        </button>
                    </div>
                );
            } else if (type === "inProgress") {
                if ([bonusStatusEnum.Pending, bonusStatusEnum.Processing].includes(bonusStatusId)) {
                    return (
                        <div className="promotion-inProgress-section">
                            <div className="promotion-note">
                                <div className="note-title">
                                    <span>ยอดหมุนเวียนสะสม</span>
                                    <span>รอดำเนินการ</span>
                                </div>
                                {bonusStatusId === bonusStatusEnum.Processing && (
                                    <div className="note-content">
                                        <InfoCircleOutlined className="info-icon" />
                                        <div>
                                            โบนัสอยู่ระหว่างการตรวจสอบ กรุณารอการอนุมัติ
                                            {/* 奖金正在审核中，请等待批准 */}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                } else if (bonusStatusId === bonusStatusEnum.RequiredCheck) {
                    return (
                        <div className="promotion-inProgress-section">
                            <div className="promotion-note">
                                <div className="note-title">
                                    <span>ยอดหมุนเวียนสะสม</span>
                                    <span className="red-note">กำลังค้นหา</span>
                                </div>
                                <div className="note-content">
                                    <InfoCircleOutlined className="info-icon" />
                                    <div>
                                        ไม่สามารถทำยอดหมุนเวียนสำหรับโปรโมชั่นนี้ครบได้
                                        <br />
                                        กรุณาติดต่อฝ่ายบริการลูกค้าเพื่อขอความช่วยเหลือ
                                        {/* 此优惠无法进行流水累计，请联系在线客服协助确认 */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                } else if (bonusStatusId === bonusStatusEnum.InProgress) {
                    return (
                        <div className="promotion-inProgress-section">
                            <div className="promotion-progress">
                                <div className="progress-info">
                                    <span className="progress-title">
                                        ยอดหมุนเวียนสะสม
                                    </span>
                                    <span className="progress-current">
                                        {numberformatter(currentProgress, 0)}
                                    </span>
                                    <span className="progress-separator">/</span>
                                    <span className="progress-total">
                                        {numberformatter(totalProgress, 0)}
                                    </span>
                                    <span className="progress-percentage">
                                        {numberformatter(percentage, 0)}%
                                    </span>
                                </div>
                                <div className="progress-bar">
                                    <div
                                        className="progress-bar-filled"
                                        style={{ width: `${numberformatter(percentage, 0)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                }
            } else if (type === "claimable") {
                return (
                    <div className="promotion-claimable-section">
                        <div className="claimable-status">
                            <span className="progress-info">
                                ยอดหมุนเวียนสะสม
                                <span className="progress-current">
                                    {numberformatter(currentProgress, 0)}
                                </span>{" "}
                                / {numberformatter(totalProgress, 0)}
                            </span>
                            <span className="claimable-status-icon">
                                <img
                                    alt="check"
                                    src="/th/img/icon/miniTick.svg"
                                    className="custom-check-icon"
                                />
                                สำเร็จ
                            </span>
                        </div>
                        <button
                            className="claim-button"
                            onClick={() => onActionClick(data, "claim")}
                        >
                            ขอรับโบนัส
                        </button>
                    </div>
                );
            } else if (type === "completed") {
                return null;
            }
        } else if (from === "PMA") {
            return (
                showFooterMessage && (
                    <div className="notification-card-footer">
                        <img
                            src="/th/img/icon/info.svg"
                            alt="info"
                            className="info"
                        />
                        <span>{statusTipsMessage}</span>
                    </div>
                )
            );
        }
    };

    return (
        <div className="my-promotion-card">
            <div className="card-header">
                <h4 className="card-title">{title}</h4>
                <CardIcons productGroups={productGroups} />
                {type === "inProgress" && (
                    <div className="more-menu-container">
                        <div
                            className="more-menu-button"
                            onClick={() => onMoreClick(data.playerBonusId)}
                        >
                            <img
                                src="/th/img/icon/icon-kebab-menu.svg"
                                alt="More options"
                            />
                        </div>
                        {showMoreMenuId === data.playerBonusId && (
                            <div className="more-menu">
                                <div
                                    className="menu-item"
                                    onClick={() => {
                                        onActionClick(data, "cancel");
                                        onMoreClick(null);
                                    }}
                                >
                                    ยกเลิก
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="card-body">{renderBody()}</div>
            <div className="card-footer">{renderFooter()}</div>
        </div>
    );
};

export default MyPromotionCard;
